import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, ChevronDown, LogOut, User as UserIcon, AlertCircle, Calendar, CheckCircle2, Clock, Menu, Search, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiGet, apiPut, apiPost } from '../lib/api';
import { toast } from 'sonner';
import GlobalSearch from './GlobalSearch';
import CompanySwitcher from './CompanySwitcher';

import { formatDate as _fmtD, formatDateTime as _fmtDT } from '../lib/format';

const fmtDate = (s) => _fmtD(s);

export default function Header({ projects = [], activeProject, setActiveProject, onOpenMenu }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [tasks, setTasks] = useState({ overdue: [], due_today: [], counts: {} });
  const [inbox, setInbox] = useState([]);
  const notifRef = useRef(null);
  const _DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const _now = new Date();
  const date = `${_DAYS[_now.getDay()]}, ${_fmtD(_now)}`;

  const loadTasks = async () => {
    try {
      // scope=mine so the bell only counts tasks owned by current user
      // (matches My Tasks page default), preventing the 2-vs-0 mismatch.
      const res = await apiGet('/crm/tasks/my?scope=mine');
      setTasks(res);
    } catch {
      // Silent: header should never block on this
    }
  };

  // db.notifications inbox — Item F (DPR department reminders, etc.)
  const loadInbox = async () => {
    try {
      const res = await apiGet('/notifications/my');
      setInbox(res.items || []);
    } catch {
      // Silent: header should never block on this
    }
  };

  // Mark a single in-app notification as read from the bell.
  const markNotifRead = async (notifId, e) => {
    e?.stopPropagation();
    if (!notifId) return;
    try {
      // Bug fix (Emergent_Combined_Fixes_28Sep2026 §3a): backend registers this as POST not PUT
      await apiPost(`/notifications/${notifId}/read`, {});
      loadInbox();
    } catch {
      toast.error('Could not mark as read');
    }
  };

  // Mark a single follow-up as done from the bell.
  const markDone = async (taskId, e) => {
    e?.stopPropagation();
    if (!taskId) return;
    try {
      await apiPut(`/crm/tasks/${taskId}/complete`, {});
      toast.success('Marked as done');
      loadTasks();
    } catch {
      toast.error('Could not mark as done');
    }
  };

  // Bulk dismiss all overdue+due_today items AND unread inbox notifications.
  const markAllRead = async () => {
    const all = [...(tasks.overdue || []), ...(tasks.due_today || [])];
    const unread = (inbox || []).filter(n => !n.read);
    if (!all.length && !unread.length) return;
    const total = all.length + unread.length;
    if (!window.confirm(`Mark ${total} item(s) as done / read?`)) return;

    let okCount = 0;
    // CRM tasks (existing behaviour unchanged)
    for (const t of all) {
      try { await apiPut(`/crm/tasks/${t.id}/complete`, {}); okCount++; } catch { /* ignore */ }
    }
    // Bell notifications (Bug fix §3b): use POST /notifications/{id}/read, not PUT
    for (const n of unread) {
      try { await apiPost(`/notifications/${n.id}/read`, {}); okCount++; } catch { /* ignore */ }
    }
    toast.success(`Marked ${okCount} of ${total} as done / read`);
    loadTasks();
    loadInbox();
  };

  useEffect(() => {
    loadTasks();
    loadInbox();
    const interval = setInterval(() => { loadTasks(); loadInbox(); }, 60_000); // refresh every minute
    return () => clearInterval(interval);
  }, []);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    if (notifOpen) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [notifOpen]);

  const overdueCount = tasks.counts?.overdue || 0;
  const dueTodayCount = tasks.counts?.due_today || 0;
  const totalAlerts = overdueCount + dueTodayCount + inbox.length;

  return (
    <header
      data-testid="top-header"
      className="bg-white border-b border-slate-200 flex items-center px-3 sm:px-5 gap-2 sm:gap-4 shrink-0"
      style={{
        // v1.0.13 — reserve the system status-bar inset on phones / PWA / AAB
        // so the white header doesn't sit behind the clock & notification icons.
        paddingTop: 'env(safe-area-inset-top)',
        minHeight: 'calc(4rem + env(safe-area-inset-top))',
      }}
    >
      {/* Mobile hamburger */}
      <button
        type="button"
        onClick={onOpenMenu}
        className="md:hidden w-10 h-10 flex items-center justify-center text-slate-700 hover:bg-slate-100 rounded-sm shrink-0"
        aria-label="Open menu"
        data-testid="mobile-menu-btn"
      >
        <Menu size={20} />
      </button>

      <GlobalSearch />

      <ProjectTypeahead projects={projects} value={activeProject} onChange={setActiveProject} />

      <CompanySwitcher />

      <div className="hidden lg:block text-xs font-mono text-slate-500 px-3 border-l border-slate-200">{date}</div>

      <div className="relative" ref={notifRef}>
        <button
          data-testid="notification-bell"
          onClick={() => setNotifOpen(!notifOpen)}
          className="relative w-10 h-10 flex items-center justify-center text-slate-500 hover:text-[#3b4872] hover:bg-slate-50 rounded-sm"
        >
          <Bell size={17} />
          {totalAlerts > 0 && (
            <span
              className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 text-[10px] font-bold flex items-center justify-center text-white rounded-full ${
                overdueCount > 0 ? 'bg-red-600' : 'bg-amber-500'
              }`}
              data-testid="notification-badge"
            >
              {totalAlerts > 99 ? '99+' : totalAlerts}
            </span>
          )}
        </button>

        {notifOpen && (
          <div className="absolute right-0 top-12 w-[calc(100vw-1rem)] max-w-[24rem] bg-white border border-slate-200 rounded-sm shadow-xl z-30" data-testid="notification-panel">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-2">
              <div className="font-display font-bold text-sm">Notifications</div>
              <div className="flex items-center gap-2">
                {totalAlerts > 0 && (
                  <button
                    type="button"
                    onClick={markAllRead}
                    className="text-[10px] text-slate-500 hover:text-[#3b4872] font-display font-bold uppercase tracking-wider"
                    data-testid="notif-mark-all-read"
                    title="Mark all overdue & due-today follow-ups as done"
                  >
                    Mark all read
                  </button>
                )}
                <Link to="/app/crm/tasks" onClick={() => setNotifOpen(false)} className="text-xs text-[#3b4872] hover:underline font-display font-bold uppercase tracking-wider">View all</Link>
              </div>
            </div>

            {totalAlerts === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-slate-500">
                <CheckCircle2 size={24} className="text-emerald-500 mx-auto mb-2" />
                You're all caught up. No tasks need attention.
              </div>
            ) : (
              <div className="max-h-[420px] overflow-y-auto">
                {inbox.length > 0 && (
                  <div>
                    <div className="px-4 py-2 bg-violet-50 border-b border-violet-100 flex items-center gap-2 text-violet-700">
                      <Bell size={13} />
                      <span className="text-[10px] font-display font-bold uppercase tracking-[0.16em]">Notifications · {inbox.length}</span>
                    </div>
                    <ul className="divide-y divide-slate-100">
                      {inbox.slice(0, 8).map(n => (
                        <li key={n.id} className="px-4 py-2.5 hover:bg-slate-50 flex items-start gap-2 group" data-testid={`notif-inbox-row-${n.id}`}>
                          <button
                            type="button"
                            onClick={() => { setNotifOpen(false); if (n.link) navigate(n.link); }}
                            className="flex-1 min-w-0 text-left"
                          >
                            <div className="text-sm font-display font-semibold text-[#0F172A] truncate">{n.title}</div>
                            <div className="text-[11px] text-slate-500 mt-0.5 truncate">{n.message}</div>
                          </button>
                          <button
                            type="button"
                            onClick={(e) => markNotifRead(n.id, e)}
                            className="shrink-0 p-1 rounded hover:bg-emerald-50 text-slate-400 hover:text-emerald-600 transition-colors"
                            data-testid={`notif-inbox-mark-read-${n.id}`}
                            title="Mark as read"
                          >
                            <Check size={14} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {overdueCount > 0 && (
                  <div>
                    <div className="px-4 py-2 bg-red-50 border-b border-red-100 flex items-center gap-2 text-red-700">
                      <AlertCircle size={13} />
                      <span className="text-[10px] font-display font-bold uppercase tracking-[0.16em]">Overdue · {overdueCount}</span>
                    </div>
                    <ul className="divide-y divide-slate-100">
                      {tasks.overdue.slice(0, 5).map(t => (
                        <li key={t.id} className="px-4 py-2.5 hover:bg-slate-50 flex items-start gap-2 group">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-display font-semibold text-[#0F172A] truncate">{t.description}</div>
                            <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2">
                              <Clock size={10} /> Due {fmtDate(t.reminder_date)}
                              {t.entity_label && <span className="truncate">· {t.entity_label}</span>}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => markDone(t.id, e)}
                            className="shrink-0 p-1 rounded hover:bg-emerald-50 text-slate-400 hover:text-emerald-600 transition-colors"
                            data-testid={`notif-mark-done-${t.id}`}
                            title="Mark this follow-up as done"
                          >
                            <Check size={14} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {dueTodayCount > 0 && (
                  <div>
                    <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 flex items-center gap-2 text-amber-700">
                      <Calendar size={13} />
                      <span className="text-[10px] font-display font-bold uppercase tracking-[0.16em]">Due Today · {dueTodayCount}</span>
                    </div>
                    <ul className="divide-y divide-slate-100">
                      {tasks.due_today.slice(0, 5).map(t => (
                        <li key={t.id} className="px-4 py-2.5 hover:bg-slate-50 flex items-start gap-2 group">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-display font-semibold text-[#0F172A] truncate">{t.description}</div>
                            <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2">
                              {t.entity_label && <span className="truncate">{t.entity_label}</span>}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => markDone(t.id, e)}
                            className="shrink-0 p-1 rounded hover:bg-emerald-50 text-slate-400 hover:text-emerald-600 transition-colors"
                            data-testid={`notif-mark-done-${t.id}`}
                            title="Mark this follow-up as done"
                          >
                            <Check size={14} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="relative">
        <button
          data-testid="user-menu-btn"
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 pl-2 pr-3 py-1.5 hover:bg-slate-50 rounded-sm"
        >
          <div className="w-8 h-8 rounded-sm bg-[#2F3E46] text-white flex items-center justify-center font-display font-bold text-xs overflow-hidden">
            {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : (user?.name || 'U').slice(0,1).toUpperCase()}
          </div>
          <div className="text-left hidden sm:block">
            <div className="text-xs font-display font-semibold leading-tight">{user?.name || 'User'}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{user?.role || 'Member'}</div>
          </div>
          <ChevronDown size={14} className="text-slate-400" />
        </button>
        {open && (
          <div className="absolute right-0 top-12 w-56 bg-white border border-slate-200 rounded-sm shadow-lg z-30 py-1">
            <div className="px-4 py-3 border-b border-slate-100">
              <div className="font-display font-semibold text-sm truncate">{user?.name}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            </div>
            <button data-testid="profile-link" onClick={() => { setOpen(false); navigate('/app/profile'); }} className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"><UserIcon size={14} /> My Profile</button>
            <button data-testid="logout-btn" onClick={logout} className="w-full text-left px-4 py-2 text-sm hover:bg-indigo-50 text-[#3b4872] flex items-center gap-2"><LogOut size={14} /> Sign out</button>
          </div>
        )}
      </div>
    </header>
  );
}


/* ─── Project Typeahead (Iter 112.2) ─────────────────────────────────────────
 * Replaces the old native <select> which stretched as wide as the longest
 * project name. This is a fixed-width searchable popover that always renders
 * at 240px wide. Click → opens dropdown with a search box + filtered list.
 * Long names truncate cleanly in the trigger with a tooltip.
 */
function ProjectTypeahead({ projects, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [anchorRect, setAnchorRect] = useState(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const selected = useMemo(() => projects.find(p => p.id === value), [projects, value]);
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return projects.slice(0, 80);
    return projects.filter(p =>
      (p.code || '').toLowerCase().includes(term) ||
      (p.name || '').toLowerCase().includes(term)
    ).slice(0, 80);
  }, [projects, q]);

  useEffect(() => {
    if (!open) return;
    const compute = () => {
      const r = wrapRef.current?.getBoundingClientRect();
      if (r) setAnchorRect({ left: r.left, top: r.bottom + 4, width: r.width });
    };
    compute();
    setTimeout(() => inputRef.current?.focus(), 50);
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    document.addEventListener('mousedown', onDoc);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
      document.removeEventListener('mousedown', onDoc);
    };
  }, [open]);

  const label = selected ? `${selected.code} — ${selected.name}` : 'All Projects';

  return (
    <div ref={wrapRef} className="hidden md:block w-60 shrink-0">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid="project-selector"
        title={label}
        className="w-full flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-sm bg-white text-sm hover:bg-slate-50"
      >
        <span className="text-[10px] font-display tracking-[0.2em] uppercase text-slate-500 shrink-0">Project</span>
        <span className="font-medium text-slate-700 truncate flex-1 text-left">{label}</span>
        <ChevronDown size={14} className="text-slate-400 shrink-0" />
      </button>

      {open && anchorRect && (
        <div
          className="bg-white border border-slate-200 rounded-sm shadow-2xl"
          style={{
            position: 'fixed',
            left: `${anchorRect.left}px`,
            top: `${anchorRect.top}px`,
            width: `${Math.max(320, anchorRect.width)}px`,
            zIndex: 9999,
          }}
          data-testid="project-typeahead-popover"
        >
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                ref={inputRef}
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search by code or name…"
                data-testid="project-typeahead-input"
                className="w-full pl-7 pr-2 py-1.5 border border-slate-200 rounded-sm text-xs focus:border-[#3b4872] focus:outline-none"
              />
            </div>
          </div>
          <div className="max-h-[320px] overflow-y-auto py-1" data-testid="project-typeahead-list">
            <button
              onClick={() => { onChange(''); setOpen(false); setQ(''); }}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 ${!value ? 'bg-slate-100 font-display font-bold' : ''}`}
            >
              All Projects
            </button>
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-xs text-slate-400 text-center">No matches</div>
            )}
            {filtered.map(p => (
              <button
                key={p.id}
                onClick={() => { onChange(p.id); setOpen(false); setQ(''); }}
                data-testid={`project-opt-${p.id}`}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 truncate ${value === p.id ? 'bg-slate-100 font-display font-bold' : ''}`}
                title={`${p.code} — ${p.name}`}
              >
                <span className="font-mono text-slate-500">{p.code}</span>
                <span className="text-slate-700 ml-2">{p.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
